import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Text,
  PhoneIcon,
  LocationIcon,
  CheckIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
  TrackingIcon,
} from '@citibox-panel/ui';

export type SearchType = 'phone' | 'address' | 'tracking';

export interface SearchTypeSelectProps {
  value: SearchType;
  onChange: (type: SearchType) => void;
}

// TODO: i18n
const TYPES: Record<SearchType, { label: string; Icon: typeof PhoneIcon }> = {
  phone: { label: 'Teléfono', Icon: PhoneIcon },
  address: { label: 'Dirección', Icon: LocationIcon },
  tracking: { label: 'Tracking', Icon: TrackingIcon },
};

// Fixes the menu's order: Object.keys over the record above comes back as bare strings.
const ORDER: readonly SearchType[] = ['phone', 'address', 'tracking'];

export function SearchTypeSelect({ value, onChange }: SearchTypeSelectProps) {
  // Menu.anchorEl is required and takes the element itself, so the popover hangs from the real
  // rendered <button> rather than the wrapper.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const active = TYPES[value];

  return (
    <>
      <Button
        ref={triggerRef}
        // Quiet by default and quiet while open: a mode switch is not an action, and the field it
        // lives in has no borders to echo. Four things about this button are the FIELD's rules and
        // not set here — the open-state fill, the grey of the pin and the chevron, the bold of the
        // label, and the air to the right of the chevron — because the façade Button takes no style
        // from a call site. That field belongs to the mode this trigger is rendered into
        // (`phone-search`, `location-search`) and reaches it through the host's `trailing` slot, so
        // the rules live there and this slice must not start restating them.
        // `size="small"` is load-bearing: a medium button stands 36.5px and the field is 34, so it
        // would stretch the control it sits inside.
        variant="text"
        color="neutral"
        size="small"
        sentenceCase
        onClick={() => {
          setOpen((isOpen) => !isOpen);
        }}
        // The visible text is the current value, so the name states the purpose and still contains
        // it (WCAG 2.5.3) — a bare «Tipo de búsqueda» would replace what a speech user can see.
        // TODO: i18n
        aria-label={`Tipo de búsqueda: ${active.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        startIcon={<active.Icon fontSize="small" />}
        endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      >
        {active.label}
      </Button>
      <Menu
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        {ORDER.map((type) => {
          const { label, Icon } = TYPES[type];
          const isActive = type === value;
          return (
            <MenuItem
              key={type}
              role="menuitemradio"
              aria-checked={isActive}
              selected={isActive}
              onClick={() => {
                onChange(type);
                setOpen(false);
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <Text color={isActive ? 'brand' : 'primary'}>{label}</Text>
              {isActive && (
                // Decorative echo of aria-checked, trailing the row as the frames show it.
                <Box sx={{ display: 'flex', ml: 'auto', pl: 2 }}>
                  <CheckIcon fontSize="small" />
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
